import React, { useEffect, useRef, useState, useCallback } from "react";
import KdsCompletedCard from "../KdsCompletedCard/KdsCompletedCard";
import { Box, CircularProgress, Modal } from "@mui/material";
import PosCustomIcon from "../../../../../customComponents/PosCustomIcon/PosCustomIcon";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/20/solid";
import PosCustomFilterV3 from "../../../../../customComponents/PosCustomFilterV3/PosCustomFilterV3";
import { filterJobs } from "../../../../../config/Services";
import { useSnackbar } from "../../../product/hooks/SnackbarProvider";
import { colorConstant } from "../../../../../styles/colorConstant/colorConstant";
import { updateJobs } from "../../../../../config/Services";
import "./RedoAcceptModal.css";

const style = {
  position: "fixed",
  top: 0,
  right: 0,
  zIndex: 60,
  display: "flex",
  height: "100%",
  width: "84vw",
  maxWidth: "1120px",
  flexDirection: "column",
  borderTopLeftRadius: "24px",
  borderBottomLeftRadius: "24px",
  borderLeft: "1px solid rgba(226, 232, 240, 0.8)",
  backgroundColor: "#ffffff",
  boxShadow: "0 28px 90px rgba(15, 23, 42, 0.22)",
  outline: "none",
  boxSizing: "border-box",
};

const emptyStateStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  gap: "16px",
  color: "#6B7280",
  width: "100%",
};

const emptyIconStyle = {
  width: "80px",
  height: "80px",
  backgroundColor: "#F3F4F6",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const RedoAcceptModal = ({ open, onClose }) => {
  const [completedData, setCompletedData] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [completedApiLoader, setCompletedApiLoader] = useState(false);
  const [reverseOrder, setReverseOrder] = useState(false);
  const [masonryColumns, setMasonryColumns] = useState(3);

  // Pagination state
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const showSnackbar = useSnackbar();
  const containerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const observerRef = useRef(null);
  const appConfigData = localStorage.getItem("appConfig");

  // Use refs to track pagination state to avoid stale closures
  const currentPageRef = useRef(1);
  const isLoadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);

  const orgId = localStorage.getItem("orgId");
  const locId = localStorage.getItem("locId");
  const pageSize = 10;

  const retailUserLocalStorageData = JSON.parse(localStorage.getItem("retailUserDetails"));
  const appConfig = appConfigData ? JSON.parse(appConfigData) : {};

  const handleReverseOrderAction = (jobId) => {
    if (!appConfig?.enabledFeatures?.REQUEUE_ORDERS) {
      showSnackbar("Requeue orders feature is disabled in POS configurations", "error");
      return Promise.reject();
    }
    const payload = {
      jobIds: [jobId] || [],
      status: "PENDING",
      locationId: locId,
      updatedBy: retailUserLocalStorageData?.uidx,
      updatedByName: retailUserLocalStorageData?.firstName,
    };
    return updateJobs(payload)
      .then((res) => {
        setReverseOrder(!reverseOrder);
        showSnackbar("Order Reversed Successfully", "success");
      })
      .catch((err) => {
        showSnackbar("Unable to reverse the order", "error");
        throw err;
      });
  };

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Reset pagination when filters or search change
  useEffect(() => {
    currentPageRef.current = 1;
    hasMoreRef.current = true;
    setCompletedData([]);
    setHasMore(true);
  }, [selectedSources, debouncedSearch]);

  const fetchCompletedJobs = useCallback(
    (page = 1, isLoadMore = false) => {
      // Prevent duplicate calls
      if (isLoadMore && isLoadingMoreRef.current) return;

      if (isLoadMore) {
        setIsLoadingMore(true);
        isLoadingMoreRef.current = true;
      } else {
        setCompletedApiLoader(true);
      }

      const payload = {
        orgId,
        locationId: locId,
        page,
        pageSize,
        statuses: ["IN_PROGRESS"],
        sortByUpdatedDate: "DESCENDING",
        ...(selectedSources?.length > 0 && { source: selectedSources?.flat() }),
        ...(debouncedSearch?.trim() && { query: debouncedSearch.trim() }),
      };

      filterJobs(payload)
        .then((res) => {
          const results = res?.data?.results || [];
          const total = res?.data?.totalResults || 0;

          setCompletedData((prev) => {
            const newData = isLoadMore ? [...prev, ...results] : results;

            // Check if there are more pages
            const hasMorePages = newData.length < total && results.length === pageSize;
            setHasMore(hasMorePages);
            hasMoreRef.current = hasMorePages;

            return newData;
          });

          setTotalCount(total);
          setCompletedApiLoader(false);
          setIsLoadingMore(false);
          isLoadingMoreRef.current = false;
        })
        .catch((err) => {
          showSnackbar("Unable to fetch the completed orders", "error");
          setCompletedApiLoader(false);
          setIsLoadingMore(false);
          isLoadingMoreRef.current = false;
        });
    },
    [selectedSources, debouncedSearch, reverseOrder]
  );

  // Initial fetch and refetch on filter/search change
  useEffect(() => {
    if (open) {
      fetchCompletedJobs(1, false);
    }
  }, [open, selectedSources, debouncedSearch, fetchCompletedJobs]);

  // IntersectionObserver for infinite vertical scroll
  useEffect(() => {
    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!open || !loadMoreSentinelRef.current || !containerRef.current) return;

    const observerCallback = (entries) => {
      const [entry] = entries;

      if (entry.isIntersecting && !isLoadingMoreRef.current && hasMoreRef.current) {
        const nextPage = currentPageRef.current + 1;
        currentPageRef.current = nextPage;
        fetchCompletedJobs(nextPage, true);
      }
    };

    observerRef.current = new IntersectionObserver(observerCallback, {
      root: containerRef.current,
      rootMargin: "0px 0px 400px 0px", // Trigger 400px before reaching the bottom
      threshold: 0,
    });

    observerRef.current.observe(loadMoreSentinelRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [open, fetchCompletedJobs, completedData.length, completedApiLoader]);

  // Responsive masonry columns observer
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width <= 768) setMasonryColumns(2);
      else setMasonryColumns(3);
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setDebouncedSearch("");
      currentPageRef.current = 1;
      hasMoreRef.current = true;
      isLoadingMoreRef.current = false;
      setCompletedData([]);
      setHasMore(true);
      setSelectedSources([]);
      setTotalCount(0);

      // Clean up observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    }
  }, [open]);

  // Empty State Component
  const EmptyState = () => {
    const isSearchOrFilterActive = debouncedSearch?.trim() || selectedSources?.length > 0;

    return (
      <div style={emptyStateStyle}>
        <div style={emptyIconStyle}>
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9CA3AF"
            strokeWidth="1.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <div style={{ textAlign: "center" }}>
          <p
            style={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#374151",
              margin: "0 0 8px 0",
            }}
          >
            {isSearchOrFilterActive ? "No matching orders found" : "No accepted orders"}
          </p>
          <p
            style={{
              fontSize: "14px",
              color: "#6B7280",
              margin: 0,
            }}
          >
            {isSearchOrFilterActive
              ? "Try adjusting your search or filter criteria"
              : "Accepted orders will appear here"}
          </p>
        </div>
        {isSearchOrFilterActive && (
          <button
            onClick={() => {
              setSearchQuery("");
              setSelectedSources([]);
            }}
            style={{
              padding: "8px 16px",
              backgroundColor: "#0860E6",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: 500,
              cursor: "pointer",
              marginTop: "8px",
            }}
          >
            Clear filters
          </button>
        )}
      </div>
    );
  };

  return (
    <div>
      <Modal
        open={open}
        onClose={onClose}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(6px)",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
          },
        }}
      >
        <Box sx={style}>
          <div className="kds-redo-accept-header">
            <div className="kds-redo-accept-header-left">
              <h2 className="kds-redo-accept-title" data-testid="text-kds-redo-accept-title">
                Accepted Orders
              </h2>
              <span className="kds-redo-accept-count" data-testid="text-kds-redo-accept-count">
                {totalCount}
              </span>
            </div>
            <button
              className="kds-redo-accept-close-btn"
              data-testid="button-close-redo-accept-drawer"
              onClick={onClose}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-x"
                aria-hidden="true"
              >
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>
          </div>

          <div className="kds-redo-accept-search-section">
            <div className="kds-redo-accept-search-grid">
              <div className="kds-redo-accept-search-wrapper">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="kds-redo-accept-search-icon"
                  aria-hidden="true"
                >
                  <path d="m21 21-4.34-4.34"></path>
                  <circle cx="11" cy="11" r="8"></circle>
                </svg>
                <input
                  type="text"
                  placeholder="Search items"
                  className="kds-redo-accept-search-input"
                  data-testid="input-kds-redo-accept-search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="kds-comp-fiter-container">
                <PosCustomFilterV3
                  styleProps={{
                    buttonLabel: "Filter By Source",
                    buttonRadius: "20px",
                    buttonWidth: "100%",
                    buttonPadding: "12px 16px",
                    buttonLabelFontWeight: 400,
                    buttonLabelColor: "#314158"
                  }}
                  options={[
                    { id: 1, label: "Zomato", value: "ZOMATO_ORDER" },
                    { id: 2, label: "Swiggy", value: "SWIGGY_ORDER" },
                    { id: 3, label: "Pos", value: ["PALLET_POS", "PALLET_MPOS"] },
                    { id: 4, label: "WhatsApp Orders", value: "WHATSAPP_ORDERS" },
                  ]}
                  selectedValues={selectedSources}
                  onChange={(filter) => {
                    setSelectedSources(filter);
                  }}
                />
              </div>
            </div>
          </div>

          {completedApiLoader ? (
            <div className="KdsCompLoaderContainer">
              <CircularProgress size={"24px"} sx={{ color: "#0860E6" }} />
            </div>
          ) : (
            <div className="kds-redo-accept-scroll-viewport" ref={containerRef}>
              {completedData?.length > 0 ? (
                <>
                  <div className="kds-redo-accept-grid">
                    {completedData?.map((item) => (
                      <KdsCompletedCard
                        mode="redo"
                        key={item?.jobId}
                        prepTime={item?.metadata?.avgPrepTime ?? "N/A"}
                        tokenNumber={item?.token}
                        source={item?.source}
                        orderType={item?.type}
                        subOrderType={item?.metadata?.subOrderType}
                        orderId={item?.externalRefId}
                        user={item?.createdByName}
                        items={item?.tasks}
                        jobId={item?.jobId}
                        updatedTime={item?.updated}
                        status={item?.status}
                        onReverseOrder={handleReverseOrderAction}
                      />
                    ))}
                  </div>

                  {/* Sentinel element for IntersectionObserver */}
                  <div
                    ref={loadMoreSentinelRef}
                    style={{
                      height: "1px",
                      width: "100%",
                    }}
                  />

                  {/* Loading indicator for pagination */}
                  {isLoadingMore && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "20px 0",
                        width: "100%",
                      }}
                    >
                      <CircularProgress size={"24px"} sx={{ color: "#0860E6" }} />
                    </div>
                  )}
                </>
              ) : (
                <EmptyState />
              )}
            </div>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default RedoAcceptModal;
